import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Save, Bell, BellOff, User, KeyRound, AtSign, Building2, FileText, Upload, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { normalizeEventTypes, settingsErrorText } from '@/components/settings/notificationLabels';
import { PhoneAlertsCard } from '@/components/settings/PhoneAlertsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserSettingsPage({ user, onLogout, onUserUpdate }) {
  const { t } = useTranslation();
  const isContractor = user?.role === "contractor";

  // Account
  const [loginEmail, setLoginEmail] = useState(user?.email || "");
  const [emailPassword, setEmailPassword] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [notificationEmail, setNotificationEmail] = useState("");
  const [savingNotifEmail, setSavingNotifEmail] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  // Notifications
  const [eventTypes, setEventTypes] = useState({});
  const [notifPrefs, setNotifPrefs] = useState({ mute_all: false, events: {} });
  const [savingNotifs, setSavingNotifs] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [preferencesError, setPreferencesError] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(true);
  const notificationTypes = normalizeEventTypes(eventTypes, user?.role);

  // Contractor company profile
  const [company, setCompany] = useState({ company_name: "", tax_number: "", legal_document_url: null, phone: "", contact_person: "" });
  const [savingCompany, setSavingCompany] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docInputRef = useRef(null);

  useEffect(() => {
    loadAccount();
    loadNotifPrefs();
    if (isContractor) loadCompanyProfile();
  }, []);

  const loadAccount = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setLoginEmail(data.email || "");
      setNotificationEmail(data.notification_email || "");
    } catch {}
  };

  const loadNotifPrefs = async () => {
    setPreferencesError(false); setLoadingPreferences(true);
    try {
      const [typesRes, prefsRes] = await Promise.all([
        axios.get(`${API}/notifications/event-types`),
        axios.get(`${API}/notifications/preferences`)
      ]);
      setEventTypes(typesRes.data);
      setNotifPrefs({ mute_all: prefsRes.data?.mute_all === true, events: prefsRes.data?.events && typeof prefsRes.data.events === 'object' ? prefsRes.data.events : {} });
    } catch { setPreferencesError(true); }
    finally { setLoadingPreferences(false); }
  };

  const loadCompanyProfile = async () => {
    try {
      const { data } = await axios.get(`${API}/contractor/company-profile`);
      setCompany(data);
    } catch {}
  };

  const saveLoginEmail = async (e) => {
    e.preventDefault();
    if (!emailPassword) { toast.error("Password required to confirm email change"); return; }
    setSavingEmail(true);
    try {
      const { data } = await axios.put(`${API}/auth/email`, { new_email: loginEmail, password: emailPassword });
      if (data.access_token) {
        localStorage.setItem("token", data.access_token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${data.access_token}`;
        if (onUserUpdate) onUserUpdate({ email: loginEmail });
      }
      toast.success("Email updated");
      setEmailPassword("");
    } catch (err) { toast.error(settingsErrorText(err.response?.data?.detail, t('personalSettings.saveFailed'))); }
    finally { setSavingEmail(false); }
  };

  const saveNotificationEmail = async (e) => {
    e.preventDefault();
    setSavingNotifEmail(true);
    try {
      if (notificationEmail.trim()) {
        await axios.put(`${API}/auth/notification-email`, { notification_email: notificationEmail });
        toast.success("Notification email saved");
      } else {
        await axios.put(`${API}/auth/notification-email`, { notification_email: "" });
        toast.success("Notification email cleared — using login email");
      }
    } catch { toast.error("Failed to save"); }
    finally { setSavingNotifEmail(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error("Passwords don't match"); return; }
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSavingPassword(true);
    try {
      await axios.put(`${API}/auth/password`, { current_password: currentPassword, new_password: newPassword });
      toast.success("Password updated");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) { toast.error(settingsErrorText(err.response?.data?.detail, t('personalSettings.saveFailed'))); }
    finally { setSavingPassword(false); }
  };

  const saveNotifPrefs = async () => {
    setSavingNotifs(true);
    try {
      await axios.put(`${API}/notifications/preferences`, { mute_all: notifPrefs.mute_all, events: notifPrefs.events });
      toast.success(<span data-testid="settings-preferences-saved">{t('personalSettings.saved')}</span>);
    } catch { toast.error("Failed to save preferences"); }
    finally { setSavingNotifs(false); }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const { data } = await axios.post(`${API}/notifications/test`);
      toast.success(data.message);
    } catch (err) { toast.error(settingsErrorText(err.response?.data?.detail, t('personalSettings.testEmailFailed'))); }
    finally { setSendingTest(false); }
  };

  const saveCompanyProfile = async (e) => {
    e.preventDefault();
    setSavingCompany(true);
    try {
      await axios.patch(`${API}/contractor/company-profile`, company);
      toast.success("Company profile updated");
    } catch { toast.error("Failed to save company profile"); }
    finally { setSavingCompany(false); }
  };

  const uploadLegalDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = res.data.url;
      setCompany(prev => ({ ...prev, legal_document_url: url }));
      await axios.patch(`${API}/contractor/company-profile`, { legal_document_url: url });
      toast.success("Document uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploadingDoc(false); if (docInputRef.current) docInputRef.current.value = ""; }
  };

  const toggleEvent = (key, defaultEnabled) => {
    setNotifPrefs(prev => ({
      ...prev,
      events: { ...prev.events, [key]: !(prev.events[key] ?? defaultEnabled) }
    }));
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-2xl min-w-0" data-testid="personal-settings-page">
        <div>
          <h1 className="font-display text-4xl font-bold uppercase text-slate-900" data-testid="personal-settings-title">{t("settings.title")}</h1>
          <p className="text-slate-500 text-sm mt-1">{t("settings.manageAccount")}</p>
        </div>

        {/* Contractor Company Profile */}
        {isContractor && (
          <Card className="border border-slate-200 rounded-sm">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="w-4 h-4 text-slate-600" /> {t("settings.companyProfile")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={saveCompanyProfile} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("settings.companyName")}</Label>
                    <Input className="mt-1 rounded-sm" value={company.company_name} onChange={e => setCompany(p => ({ ...p, company_name: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("settings.taxNumber")}</Label>
                    <Input className="mt-1 rounded-sm" placeholder="e.g. MK4030012345" value={company.tax_number} onChange={e => setCompany(p => ({ ...p, tax_number: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Phone</Label>
                    <Input className="mt-1 rounded-sm" value={company.phone} onChange={e => setCompany(p => ({ ...p, phone: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("settings.contactPerson")}</Label>
                    <Input className="mt-1 rounded-sm" value={company.contact_person} onChange={e => setCompany(p => ({ ...p, contact_person: e.target.value }))} />
                  </div>
                </div>
                {/* Legal Document */}
                <div>
                  <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("settings.legalDocument")}</Label>
                  {company.legal_document_url ? (
                    <div className="mt-1 flex items-center gap-3 p-2 border border-slate-200 rounded-sm bg-slate-50">
                      <Paperclip className="w-4 h-4 text-slate-400" />
                      <a href={company.legal_document_url} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline truncate flex-1">{t("settings.viewDocument")}</a>
                      <button type="button" className="text-xs text-slate-400 hover:text-red-500" onClick={() => setCompany(p => ({ ...p, legal_document_url: null }))}>{t("settings.remove")}</button>
                    </div>
                  ) : (
                    <label className="mt-1 flex items-center justify-center w-full border-2 border-dashed border-slate-200 rounded-sm p-3 cursor-pointer hover:border-slate-300 transition-colors">
                      <Upload className="w-4 h-4 text-slate-400 mr-2" />
                      <span className="text-xs text-slate-500">{uploadingDoc ? t("settings.saving") : t("settings.uploadPdfOrImage")}</span>
                      <input ref={docInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={uploadLegalDoc} disabled={uploadingDoc} />
                    </label>
                  )}
                </div>
                <Button type="submit" disabled={savingCompany} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-sm">
                  <Save className="w-3.5 h-3.5 mr-1.5" /> {savingCompany ? t("settings.saving") : t("settings.saveCompanyProfile")}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Login Email */}
        <Card className="border border-slate-200 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><AtSign className="w-4 h-4 text-slate-600" /> {t("settings.loginEmail")}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={saveLoginEmail} className="space-y-3">
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("settings.newEmail")}</Label>
                <Input data-testid="settings-login-email" aria-label={t('settings.newEmail')} className="mt-1 rounded-sm" type="email" required value={loginEmail} onChange={e => setLoginEmail(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("settings.confirmWithPassword")}</Label>
                <Input data-testid="settings-email-password" aria-label={t('settings.confirmWithPassword')} className="mt-1 rounded-sm" type="password" required value={emailPassword} onChange={e => setEmailPassword(e.target.value)} placeholder={t('settings.currentPassword')} />
              </div>
              <Button type="submit" data-testid="settings-save-login-email" disabled={savingEmail} variant="outline" className="rounded-sm text-sm">
                {savingEmail ? t("settings.saving") : t("settings.updateEmail")}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Password */}
        <Card className="border border-slate-200 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><KeyRound className="w-4 h-4 text-slate-600" /> {t("settings.changePassword")}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={savePassword} className="space-y-3">
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("settings.currentPassword")}</Label>
                <Input data-testid="settings-current-password" aria-label={t('settings.currentPassword')} className="mt-1 rounded-sm" type="password" required value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("settings.newPassword")}</Label>
                  <Input data-testid="settings-new-password" aria-label={t('settings.newPassword')} className="mt-1 rounded-sm" type="password" required minLength={6} value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("settings.confirm")}</Label>
                  <Input data-testid="settings-confirm-password" aria-label={t('settings.confirm')} className="mt-1 rounded-sm" type="password" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
                </div>
              </div>
              <Button type="submit" data-testid="settings-save-password" disabled={savingPassword} variant="outline" className="rounded-sm text-sm">
                {savingPassword ? t("settings.saving") : t("settings.updatePassword")}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Notification Email */}
        <Card className="border border-slate-200 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Bell className="w-4 h-4 text-slate-600" /> {t("settings.emailNotifications")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={saveNotificationEmail} className="space-y-3">
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("settings.notificationEmail")}</Label>
                <p className="text-[11px] text-slate-400 mb-1">{t("settings.notificationEmailHint")}</p>
                <Input data-testid="settings-notification-email" aria-label={t('settings.notificationEmail')} className="rounded-sm" type="email" value={notificationEmail} onChange={e => setNotificationEmail(e.target.value)} placeholder="notifications@example.com" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="submit" data-testid="settings-save-notification-email" disabled={savingNotifEmail} variant="outline" className="rounded-sm text-sm">
                  {savingNotifEmail ? t("settings.saving") : t("settings.saveEmail")}
                </Button>
                <Button type="button" data-testid="settings-test-email" variant="outline" size="sm" disabled={sendingTest} onClick={sendTestEmail} className="rounded-sm text-xs">
                  {t("settings.sendTestEmail")}
                </Button>
              </div>
            </form>

            {/* Mute All */}
            <div className="flex items-center justify-between gap-4 py-2 border-t border-slate-100">
              <div>
                <p className="text-sm font-medium text-slate-800">{t("settings.muteAllNotifications")}</p>
                <p className="text-xs text-slate-400">{t("settings.disableAllNotifications")}</p>
              </div>
              <Switch data-testid="settings-mute-notifications" aria-label={t('settings.muteAllNotifications')} disabled={loadingPreferences || preferencesError} checked={notifPrefs.mute_all} onCheckedChange={v => setNotifPrefs(p => ({ ...p, mute_all: v }))} className="shrink-0" />
            </div>

            {preferencesError && <div role="alert" data-testid="settings-preferences-error" className="space-y-2 text-sm text-red-700"><p>{t('personalSettings.loadFailed')}</p><Button onClick={loadNotifPrefs} variant="outline" data-testid="settings-preferences-retry">{t('attorneyDashboard.retry')}</Button></div>}
            {loadingPreferences && <p role="status" data-testid="settings-preferences-loading">{t('common.loading')}</p>}
            {!loadingPreferences && !preferencesError && !notifPrefs.mute_all && notificationTypes.map(({ key, label, defaultEnabled }) => (
              <div key={key} className="flex items-center justify-between gap-4 py-1.5">
                <span className="text-sm text-slate-700" data-testid={`settings-event-label-${key}`}>{t(`personalSettings.eventLabels.${key}`, { defaultValue: label })}</span>
                <Switch
                  data-testid={`settings-event-${key}`} aria-label={t(`personalSettings.eventLabels.${key}`, { defaultValue: label })} className="shrink-0"
                  checked={notifPrefs.events?.[key] ?? defaultEnabled}
                  onCheckedChange={() => toggleEvent(key, defaultEnabled)}
                />
              </div>
            ))}

            <Button onClick={saveNotifPrefs} data-testid="settings-save-preferences" disabled={savingNotifs || loadingPreferences || preferencesError} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-sm w-full whitespace-normal h-auto min-h-10">
              <Save className="w-3.5 h-3.5 mr-1.5" /> {savingNotifs ? t("settings.saving") : t("settings.savePreferences")}
            </Button>
          </CardContent>
        </Card>
        <PhoneAlertsCard />
      </div>
    </Layout>
  );
}
